/** Bounded delivery evidence only: recent callbacks are candidates, never proof of cause. */
const LABELS = ['LiquidGlass', 'PrismBackground', 'MeshFlowBackground', 'LookbookGallery', 'LoginDialog.GoogleButton'] as const;
type ObserverLabel = typeof LABELS[number];
const WINDOW_MS = 1000;
const deliveries: { label: ObserverLabel; at: number }[] = [];
const MAX_DELIVERIES = 32;

export function createDiagnosedResizeObserver(label: ObserverLabel, callback: ResizeObserverCallback): ResizeObserver {
  return new ResizeObserver((entries, observer) => {
    // Runtime allowlist also protects against accidental dynamic labels from JS callers.
    if ((LABELS as readonly string[]).includes(label)) {
      const at = Date.now();
      deliveries.push({ label, at });
      if (deliveries.length > MAX_DELIVERIES) deliveries.shift();
    }
    // Preserve native timing, exceptions and arguments. Do not hide callback failures.
    callback(entries, observer);
  });
}

export function getResizeObserverDiagnostics(error?: Error): Record<string, string | number> {
  if (error?.message !== 'ResizeObserver loop completed with undelivered notifications.'
    && error?.message !== 'ResizeObserver loop limit exceeded') return {};
  const now = Date.now();
  const recent = LABELS.flatMap(label => {
    const count = deliveries.filter(record => record.label === label && now >= record.at && now - record.at <= WINDOW_MS).length;
    return count ? [{ label, count }] : [];
  });
  return {
    errorKind: 'resize_observer_loop',
    resizeObserverAttribution: recent.length ? 'recent_delivery_candidates_not_cause' : 'unobserved',
    resizeObserverCandidateWindowMs: WINDOW_MS,
    resizeObserverCandidates: recent.map(record => record.label).join(','),
    resizeObserverDeliveryCounts: recent.map(record => `${record.label}:${record.count}`).join(','),
  };
}
